import { useEffect, useRef, useState } from "react";
import { Check, Copy, Phone } from "lucide-react";
import localization from "../../../../../assets/localization";
import Analytics from "../../../../../utils/Analytics";
import { dialableDigits, displayNumber, telHref } from "./voiceCallNumber";

/**
 * "Call us" inside the welcome message: the hotel's own voice line, answered
 * by the voice assistant. The voice channel is in beta and this card is how
 * guests find it, so it is meant to be noticed: a white card on the bubble, a
 * brand-coloured handset with a slow sonar ring, and a "new" tag.
 *
 * On a phone the whole card is a `tel:` link and dials straight away. On a
 * desktop there is usually nothing to dial with, so the number is printed
 * large enough to read and dial by hand, and a press copies it.
 *
 * Shown only when the backend sends `voiceCallNumber`, which it does only for
 * a hotel that turned the button on AND has an enabled voice line.
 */
export default function VoiceCallCard({
  number,
  isMobile,
  language,
}: {
  number?: string | null;
  isMobile: boolean;
  language: string;
}) {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const digits = dialableDigits(number);
  if (!digits) return null;

  const t = localization.get(language).voice_call;
  const href = telHref(digits);
  const shown = displayNumber(digits);

  const body = (
    <>
      <span className="voice-call-row">
        <span className="voice-call-icon" aria-hidden="true">
          <Phone size={18} strokeWidth={2.2} />
        </span>
        <span className="voice-call-text">
          <span className="voice-call-title">
            {t.title}
            <span className="voice-call-badge">{t.badge}</span>
          </span>
          <span className="voice-call-number">{shown}</span>
          <span className="voice-call-subtitle">{t.subtitle}</span>
        </span>
        {!isMobile && (
          <span className="voice-call-copy" aria-hidden="true">
            {copied ? <Check size={16} strokeWidth={2.4} /> : <Copy size={16} strokeWidth={2.2} />}
          </span>
        )}
      </span>
      {/* Its own full-width row: beside the number it pushed the number off
          the card on a 390px phone. */}
      {isMobile && (
        <span className="voice-call-action">
          <Phone size={16} strokeWidth={2.4} aria-hidden="true" />
          {t.call}
        </span>
      )}
    </>
  );

  if (isMobile) {
    return (
      <a
        className="voice-call-card"
        href={href}
        aria-label={`${t.call} ${shown}`}
        onClick={() =>
          Analytics.track("link_click", {
            url: href,
            cta: "voice_call",
            source: "welcome",
          })
        }
      >
        {body}
      </a>
    );
  }

  const copy = () => {
    Analytics.track("link_click", {
      url: href,
      cta: "voice_call_copy",
      source: "welcome",
    });
    // The number stays on screen either way, so a host page that blocks the
    // clipboard costs the guest nothing but the confirmation.
    navigator.clipboard
      ?.writeText(`+${digits}`)
      .then(() => {
        setCopied(true);
        clearTimeout(resetTimer.current);
        resetTimer.current = setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  };

  return (
    <button
      type="button"
      className={"voice-call-card" + (copied ? " is-copied" : "")}
      onClick={copy}
      title={copied ? t.copied : t.copy}
      aria-label={`${t.title}: ${shown}. ${copied ? t.copied : t.copy}`}
    >
      {body}
    </button>
  );
}
